export const anatomy2Module = {
  id: "anatomy2",
  title: "Thyroid Anatomy: Part 2",
  sections: [
    // ── Fibrous Capsule & Berry's Ligament ──────────────────────────────────
    {
      type: "text",
      title: "Anatomy",
      content: `<h3>Fibrous Capsule & Berry's Ligament</h3>
      <p>The thyroid gland is enclosed by a thin <strong>fibrous capsule</strong>. Extensions of this capsule — called <strong>septa</strong> (or trabeculae) — project inward and divide the gland into irregular <strong>lobules</strong>.</p>
      <p>On the posteromedial surface of the thyroid, the fibrous capsule thickens to form the <strong>suspensory ligament of Berry</strong> (Berry's ligament). This ligament firmly anchors the thyroid to the <strong>cricoid cartilage</strong> and the upper rings of the <strong>trachea</strong>.</p>
      <p>Because of this attachment, the thyroid gland <strong>moves upward with swallowing</strong> — a key clinical sign used to distinguish thyroid masses from other neck masses.</p>
      <p>Berry's ligament is surgically important because the <strong>recurrent laryngeal nerve</strong> passes in close proximity to it, making the nerve vulnerable to injury during thyroidectomy.</p>
      <div class="highlight-box">Key points about the fibrous capsule:
        <ul>
          <li>Fibrous capsule: <em>encloses the thyroid gland</em></li>
          <li>Septa (trabeculae): <em>project inward from the capsule, dividing the gland into lobules</em></li>
          <li>Berry's ligament: <em>anchors the thyroid to the cricoid cartilage and upper tracheal rings</em></li>
          <li>Clinical significance: <em>thyroid masses move with swallowing; RLN is at risk near Berry's ligament</em></li>
        </ul>
      </div>`,
    },
    {
      type: "question",
      text: "What structure does Berry's ligament attach the thyroid gland to?",
      options: [
        "Cricoid cartilage and upper tracheal rings",
        "Thyroid cartilage and hyoid bone",
        "Manubrium and first rib",
        "Hyoid bone and cricoid cartilage",
      ],
      correct: 0,
      explanation:
        "Berry's ligament (the suspensory ligament of Berry) anchors the posteromedial thyroid to the cricoid cartilage and upper rings of the trachea.",
    },
    {
      type: "question",
      text: "Why does the thyroid gland move upward with swallowing?",
      options: [
        "It is anchored to the cricoid cartilage and trachea via Berry's ligament",
        "It is attached to the hyoid bone via the thyroglossal duct",
        "It is embedded within the laryngeal muscles",
        "It is surrounded by the infrahyoid muscles",
      ],
      correct: 0,
      explanation:
        "Berry's ligament firmly attaches the thyroid to the cricoid cartilage and trachea. When these structures rise during swallowing, the thyroid moves with them.",
    },
    {
      type: "question",
      text: "What is the surgical significance of Berry's ligament?",
      options: [
        "The recurrent laryngeal nerve passes close to it and is at risk of injury",
        "The superior thyroid artery runs through it",
        "The external laryngeal nerve is embedded within it",
        "The inferior thyroid vein crosses it",
      ],
      correct: 0,
      explanation:
        "The recurrent laryngeal nerve runs in close proximity to Berry's ligament. This makes it vulnerable to injury during thyroidectomy, particularly when ligating nearby vessels.",
    },

    // ── Parathyroid Glands ──────────────────────────────────────────────────
    {
      type: "text",
      title: "Anatomy",
      content: `<h3>Parathyroid Glands</h3>
      <p>Closely related to the thyroid are the <strong>parathyroid glands</strong>. There are typically <strong>four parathyroid glands</strong> — two superior and two inferior — embedded within the posterior surface of the thyroid capsule.</p>
      <p>The <strong>superior parathyroid glands</strong> arise from the <span class="glossary-term">4th pharyngeal pouch<span class="tooltip">A paired structure in the embryo that gives rise to the superior parathyroid glands.</span></span> and are relatively constant in position.</p>
      <p>The <strong>inferior parathyroid glands</strong> arise from the <span class="glossary-term">3rd pharyngeal pouch<span class="tooltip">A paired structure in the embryo that gives rise to the inferior parathyroid glands and the thymus. Because they descend a longer distance during development, their final position is more variable.</span></span> — the same pouch as the thymus — and have a more variable location.</p>
      <p>The parathyroid glands produce <strong>parathyroid hormone (PTH)</strong>, which raises blood calcium levels. Because they sit on the posterior capsule of the thyroid, they are at risk of being <strong>inadvertently removed or devascularized</strong> during thyroidectomy, which can lead to <strong>hypoparathyroidism</strong> and <strong>hypocalcemia</strong>.</p>
      <div class="mnemonic-card">
        <div class="mnemonic-title">Parathyroid embryology mnemonic</div>
        <div class="mnemonic-content">
          <p><strong>"3 descends further than 4"</strong></p>
          <ul>
            <li>3rd pharyngeal pouch → <b class="purple-term">inferior</b> parathyroids (migrate further down)</li>
            <li>4th pharyngeal pouch → <b class="purple-term">superior</b> parathyroids (stay higher)</li>
          </ul>
        </div>
      </div>`,
    },
    {
      type: "question",
      text: "How many parathyroid glands does a person typically have?",
      options: ["2", "4", "6", "8"],
      correct: 1,
      explanation:
        "There are typically four parathyroid glands — two superior and two inferior — located on the posterior surface of the thyroid capsule.",
    },
    {
      type: "question",
      text: "Which pharyngeal pouch gives rise to the superior parathyroid glands?",
      options: [
        "1st pharyngeal pouch",
        "2nd pharyngeal pouch",
        "3rd pharyngeal pouch",
        "4th pharyngeal pouch",
      ],
      correct: 3,
      explanation:
        "The superior parathyroid glands arise from the 4th pharyngeal pouch. The inferior parathyroids arise from the 3rd pharyngeal pouch (the same pouch that gives rise to the thymus).",
    },
    {
      type: "question",
      text: "What complication can arise from inadvertent removal of parathyroid glands during thyroidectomy?",
      options: [
        "Hyperthyroidism",
        "Hypoparathyroidism and hypocalcemia",
        "Hyperparathyroidism and hypercalcemia",
        "Diabetes insipidus",
      ],
      correct: 1,
      explanation:
        "Inadvertent removal or devascularization of the parathyroid glands causes hypoparathyroidism, leading to decreased PTH and hypocalcemia. This can present with numbness, tingling, muscle cramps, and tetany.",
    },
    {
      type: "question",
      text: "Why do the inferior parathyroid glands have a more variable location than the superior glands?",
      options: [
        "They arise from the 3rd pharyngeal pouch and migrate a greater distance during development",
        "They arise from the 4th pharyngeal pouch and are pushed down by the thyroid",
        "They develop later in fetal life and have less time to reach a fixed position",
        "They are embedded deeper within the thyroid parenchyma",
      ],
      correct: 0,
      explanation:
        "The inferior parathyroid glands arise from the 3rd pharyngeal pouch, the same pouch as the thymus. Because they migrate a greater distance during development, their final position is more variable compared to the superior parathyroids.",
    },

    // ── Recurrent Laryngeal Nerve ───────────────────────────────────────────
    {
      type: "text",
      title: "Anatomy",
      content: `<h3>Recurrent Laryngeal Nerve</h3>
      <p>The <strong>recurrent laryngeal nerve (RLN)</strong> is a branch of the <strong>vagus nerve (CN X)</strong>. It innervates all intrinsic muscles of the larynx <em>except</em> the cricothyroid muscle, and provides sensory innervation to the larynx below the vocal folds.</p>
      <p>The left and right RLN have different courses:</p>
      <p>The <strong>left RLN</strong> loops under the <strong>aortic arch</strong> (at the level of the ligamentum arteriosum) and ascends in the <strong>tracheoesophageal groove</strong> to reach the larynx — giving it a longer course than the right.</p>
      <p>The <strong>right RLN</strong> loops under the <strong>right subclavian artery</strong> and takes a shorter, more oblique path to the larynx.</p>
      <p>Both nerves run <strong>posterior to the thyroid gland</strong>, close to Berry's ligament and the inferior thyroid artery. This makes them highly vulnerable during thyroid surgery.</p>
      <p>Damage to one RLN causes <strong>ipsilateral vocal cord paralysis</strong>, resulting in <strong>hoarseness</strong>. Damage to both RLNs causes <strong>aphonia</strong> and potentially life-threatening <strong>airway obstruction</strong>.</p>
      <div class="mnemonic-card">
        <div class="mnemonic-title">RLN course mnemonic</div>
        <div class="mnemonic-content">
          <p><strong>"Right = Right subclavian, Left = Lower (aorta)"</strong></p>
          <ul>
            <li><b class="purple-term">Left RLN</b> → loops under the aortic arch → longer course</li>
            <li><b class="purple-term">Right RLN</b> → loops under the right subclavian artery → shorter course</li>
            <li>Both ascend in the <b class="purple-term">tracheoesophageal groove</b></li>
          </ul>
        </div>
      </div>`,
    },
    {
      type: "question",
      text: "Which structure does the left recurrent laryngeal nerve loop around?",
      options: [
        "Right subclavian artery",
        "Left subclavian artery",
        "Aortic arch",
        "Brachiocephalic trunk",
      ],
      correct: 2,
      explanation:
        "The left RLN loops under the aortic arch (at the level of the ligamentum arteriosum) and ascends in the tracheoesophageal groove to reach the larynx.",
    },
    {
      type: "question",
      text: "Which structure does the right recurrent laryngeal nerve loop around?",
      options: [
        "Aortic arch",
        "Right subclavian artery",
        "Left subclavian artery",
        "Brachiocephalic vein",
      ],
      correct: 1,
      explanation:
        "The right RLN loops under the right subclavian artery and takes a shorter, more oblique course to the larynx.",
    },
    {
      type: "question",
      text: "What is the result of unilateral damage to the recurrent laryngeal nerve?",
      options: [
        "Loss of high-pitched phonation only",
        "Ipsilateral vocal cord paralysis causing hoarseness",
        "Bilateral vocal cord paralysis causing aphonia",
        "Loss of sensation below the vocal folds only",
      ],
      correct: 1,
      explanation:
        "Damage to one recurrent laryngeal nerve causes ipsilateral vocal cord paralysis, resulting in hoarseness. Bilateral RLN damage causes aphonia and can lead to life-threatening airway obstruction.",
    },
    {
      type: "question",
      text: "Which intrinsic laryngeal muscle is NOT innervated by the recurrent laryngeal nerve?",
      options: [
        "Thyroarytenoid",
        "Posterior cricoarytenoid",
        "Cricothyroid",
        "Lateral cricoarytenoid",
      ],
      correct: 2,
      explanation:
        "The cricothyroid muscle is the only intrinsic laryngeal muscle not innervated by the recurrent laryngeal nerve. It is innervated by the external branch of the superior laryngeal nerve.",
    },

    // ── Superior Laryngeal Nerve ────────────────────────────────────────────
    {
      type: "text",
      title: "Anatomy",
      content: `<h3>Superior Laryngeal Nerve</h3>
      <p>The <strong>superior laryngeal nerve (SLN)</strong> is also a branch of the <strong>vagus nerve (CN X)</strong>. It arises from the inferior ganglion of the vagus and descends toward the larynx, where it divides into two branches:</p>
      <p>1. The <strong>internal branch</strong> pierces the <strong>thyrohyoid membrane</strong> and provides <strong>sensory innervation</strong> to the laryngeal mucosa above the vocal folds, including the epiglottis.</p>
      <p>2. The <strong>external branch</strong> descends alongside the <strong>superior thyroid artery</strong> before innervating the <strong>cricothyroid muscle</strong> — the only intrinsic laryngeal muscle not supplied by the recurrent laryngeal nerve. The cricothyroid muscle tenses the vocal folds, enabling high-pitched phonation.</p>
      <p>Because the external branch of the SLN runs close to the superior thyroid artery, it can be damaged during ligation of that artery in thyroid surgery, causing <strong>loss of high-pitched phonation</strong>.</p>
      <div class="highlight-box">Superior laryngeal nerve branches:
        <ul>
          <li>Internal branch: <em>sensory — larynx above the vocal folds, pierces the thyrohyoid membrane</em></li>
          <li>External branch: <em>motor — cricothyroid muscle, runs alongside the superior thyroid artery</em></li>
        </ul>
      </div>`,
    },
    {
      type: "question",
      text: "What does the external branch of the superior laryngeal nerve innervate?",
      options: [
        "All intrinsic laryngeal muscles",
        "Laryngeal mucosa above the vocal folds",
        "Cricothyroid muscle",
        "Posterior cricoarytenoid muscle",
      ],
      correct: 2,
      explanation:
        "The external branch of the superior laryngeal nerve innervates the cricothyroid muscle — the only intrinsic laryngeal muscle not supplied by the recurrent laryngeal nerve.",
    },
    {
      type: "question",
      text: "Which structure does the internal branch of the superior laryngeal nerve pierce?",
      options: [
        "Cricothyroid membrane",
        "Thyrohyoid membrane",
        "Thyroid cartilage",
        "Hyoepiglottic ligament",
      ],
      correct: 1,
      explanation:
        "The internal branch of the SLN pierces the thyrohyoid membrane to provide sensory innervation to the laryngeal mucosa above the vocal folds.",
    },
    {
      type: "question",
      text: "Which artery runs alongside the external branch of the superior laryngeal nerve?",
      options: [
        "Inferior thyroid artery",
        "Thyroid ima artery",
        "Superior thyroid artery",
        "Common carotid artery",
      ],
      correct: 2,
      explanation:
        "The external branch of the SLN runs alongside the superior thyroid artery. During ligation of the superior thyroid artery in thyroid surgery, the external branch can be inadvertently damaged, resulting in loss of high-pitched phonation.",
    },

    // ── Lymphatic Drainage ──────────────────────────────────────────────────
    {
      type: "text",
      title: "Anatomy",
      content: `<h3>Thyroid Lymphatic Drainage</h3>
      <p>The thyroid gland has a rich lymphatic network. Lymph drains into several groups of nodes:</p>
      <p>1. <strong>Pre-laryngeal (Delphian) nodes</strong>: Located in the midline, anterior to the cricothyroid membrane. They receive early lymphatic drainage from the upper thyroid. An enlarged Delphian node can be an early sign of <strong>papillary thyroid carcinoma</strong> or thyroiditis.</p>
      <p>2. <strong>Pre-tracheal nodes</strong>: Located anterior to the trachea. They receive lymph from the isthmus and lower thyroid lobes.</p>
      <p>3. <strong>Para-tracheal nodes</strong>: Located alongside the trachea, draining alongside the recurrent laryngeal nerve. They receive lymph from the lower thyroid lobes.</p>
      <p>4. <strong>Deep cervical (jugular) nodes</strong>: All of the above eventually drain into the deep cervical lymph nodes along the internal jugular vein, and then into the <strong>thoracic duct</strong> (left side) or <strong>right lymphatic duct</strong> (right side).</p>
      <p>This is clinically important because <strong>thyroid cancer</strong> — especially papillary thyroid carcinoma — commonly spreads via the lymphatics to central and lateral neck compartments.</p>
      <div class="mnemonic-card">
        <div class="mnemonic-title">Thyroid lymphatic drainage</div>
        <div class="mnemonic-content">
          <ul>
            <li>Upper thyroid → <b class="purple-term">Pre-laryngeal (Delphian) nodes</b></li>
            <li>Isthmus/lower lobes → <b class="purple-term">Pre-tracheal nodes</b></li>
            <li>Lower lobes → <b class="purple-term">Para-tracheal nodes</b></li>
            <li>All → <b class="purple-term">Deep cervical nodes</b> → thoracic duct (L) / right lymphatic duct (R)</li>
          </ul>
        </div>
      </div>`,
    },
    {
      type: "question",
      text: "What is the clinical significance of an enlarged Delphian node?",
      options: [
        "It indicates infection of the parathyroid glands",
        "It can be an early sign of papillary thyroid carcinoma or thyroiditis",
        "It suggests damage to the recurrent laryngeal nerve",
        "It indicates obstruction of the superior thyroid vein",
      ],
      correct: 1,
      explanation:
        "The Delphian node is a pre-laryngeal node that receives early lymphatic drainage from the upper thyroid. Enlargement is associated with papillary thyroid carcinoma and thyroiditis, and may be palpable as a midline neck mass.",
    },
    {
      type: "question",
      text: "Where do para-tracheal lymph nodes receive their lymph from?",
      options: [
        "The upper poles of the thyroid only",
        "The isthmus only",
        "The lower thyroid lobes, draining alongside the recurrent laryngeal nerve",
        "Directly from the thoracic duct",
      ],
      correct: 2,
      explanation:
        "Para-tracheal nodes receive lymph from the lower thyroid lobes and drain alongside the recurrent laryngeal nerve, making them relevant in the spread of lower-pole thyroid cancers.",
    },
    {
      type: "question",
      text: "Which type of thyroid cancer most commonly spreads via lymphatic drainage to cervical lymph nodes?",
      options: [
        "Follicular thyroid carcinoma",
        "Anaplastic thyroid carcinoma",
        "Medullary thyroid carcinoma",
        "Papillary thyroid carcinoma",
      ],
      correct: 3,
      explanation:
        "Papillary thyroid carcinoma is the most common thyroid cancer and characteristically spreads via the lymphatics to cervical lymph nodes. Follicular thyroid carcinoma, by contrast, tends to spread hematogenously.",
    },

    // ── Thyroglossal Duct Cyst ──────────────────────────────────────────────
    {
      type: "text",
      title: "Anatomy",
      content: `<h3>Thyroglossal Duct Cyst</h3>
      <p>As covered in the first anatomy module, the thyroid gland descends from the base of the tongue to the neck via the <strong>thyroglossal duct</strong>. Normally, this duct <strong>involutes and disappears</strong> once the thyroid reaches its final position.</p>
      <p>If part of the duct fails to involute, it can form a <strong>thyroglossal duct cyst</strong> — the most common congenital neck mass in children and young adults.</p>
      <p>The cyst is typically located in the <strong>midline of the neck</strong>, most commonly at or just below the level of the <strong>hyoid bone</strong>, but it can appear anywhere along the path of descent from the tongue base to the thyroid.</p>
      <p>The hallmark clinical feature is that the cyst <strong>moves upward with both swallowing and tongue protrusion</strong>. Because the tract passes through or near the hyoid bone and is anchored to the tongue base, sticking the tongue out pulls the cyst upward — this distinguishes it from other neck masses.</p>
      <p>Thyroglossal duct cysts can become infected, presenting as a painful, fluctuant midline swelling. Treatment is the <strong>Sistrunk procedure</strong>: removal of the cyst together with the central portion of the hyoid bone, which significantly reduces the risk of recurrence.</p>
      <div class="highlight-box">Key features of thyroglossal duct cysts:
        <ul>
          <li>Most common congenital neck mass</li>
          <li>Location: <em>midline, most common at or just below the hyoid bone</em></li>
          <li>Classic sign: <em>moves with swallowing AND tongue protrusion</em></li>
          <li>Treatment: <em>Sistrunk procedure (cyst + central portion of hyoid bone)</em></li>
        </ul>
      </div>`,
    },
    {
      type: "question",
      text: "What is the most common congenital neck mass?",
      options: [
        "Branchial cleft cyst",
        "Thyroglossal duct cyst",
        "Cystic hygroma",
        "Dermoid cyst",
      ],
      correct: 1,
      explanation:
        "Thyroglossal duct cysts are the most common congenital neck masses. They result from failure of the thyroglossal duct to involute after the thyroid descends to its final position.",
    },
    {
      type: "question",
      text: "A child presents with a painless midline neck mass that moves upward when they swallow and when they stick out their tongue. What is the most likely diagnosis?",
      options: [
        "Branchial cleft cyst",
        "Enlarged lymph node",
        "Thyroglossal duct cyst",
        "Ectopic thyroid gland",
      ],
      correct: 2,
      explanation:
        "The classic presentation of a thyroglossal duct cyst is a midline neck mass that moves with both swallowing and tongue protrusion. The tongue protrusion sign distinguishes it from other neck masses, as the tract is anchored to the tongue base via the foramen cecum.",
    },
    {
      type: "question",
      text: "What is the Sistrunk procedure?",
      options: [
        "Drainage of the infected cyst and antibiotic therapy",
        "Removal of the thyroglossal duct cyst only",
        "Removal of the cyst along with the central portion of the hyoid bone",
        "Removal of the cyst and the entire hyoid bone",
      ],
      correct: 2,
      explanation:
        "The Sistrunk procedure involves removal of the thyroglossal duct cyst along with the central portion (body) of the hyoid bone. Removing the hyoid segment is essential because the tract passes through it — leaving it behind significantly increases the risk of recurrence.",
    },
    {
      type: "question",
      text: "Where is a thyroglossal duct cyst most commonly located?",
      options: [
        "At the base of the tongue",
        "Lateral to the sternocleidomastoid muscle",
        "At or just below the level of the hyoid bone, in the midline",
        "In the suprasternal notch",
      ],
      correct: 2,
      explanation:
        "Thyroglossal duct cysts most commonly occur at or just below the level of the hyoid bone in the midline, although they can appear anywhere along the descent path of the thyroid from the base of the tongue.",
    },
  ],
};
